import { useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { Calculator, LayoutDashboard, ArrowDownToLine, ArrowUpFromLine, Wallet, FileBarChart, Tag, BadgeAlert, Settings, LogOut, Building2, FileSpreadsheet, ShoppingCart, Menu, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';

const NAV = [
  { to: '/financas/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/financas/vendas', label: 'Vendas / OS', icon: ShoppingCart },
  { to: '/financas/clientes', label: 'Clientes', icon: Users },
  { to: '/financas/entradas', label: 'Entradas', icon: ArrowDownToLine },
  { to: '/financas/saidas', label: 'Saídas', icon: ArrowUpFromLine },
  { to: '/financas/fluxo-caixa', label: 'Fluxo de Caixa', icon: Wallet },
  { to: '/financas/contas', label: 'Contas a Pagar/Receber', icon: BadgeAlert },
  { to: '/financas/categorias', label: 'Categorias', icon: Tag },
  { to: '/financas/importar', label: 'Importar Extrato', icon: FileSpreadsheet },
  { to: '/financas/relatorios', label: 'Relatórios', icon: FileBarChart },
  { to: '/financas/mei', label: 'Status MEI', icon: Calculator },
  { to: '/financas/configuracoes', label: 'Configurações', icon: Settings },
];

function NavList({ onNavigate, empresa, user, onLogout }) {
  return (
    <>
      <div className="px-6 py-5 border-b border-white/5 flex items-center gap-3">
        <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-emerald-700 rounded-lg flex items-center justify-center">
          <Calculator className="w-5 h-5 text-white" />
        </div>
        <div>
          <p className="text-white font-bold text-sm leading-tight">Financeiro</p>
          <p className="text-emerald-300/60 text-xs">MEI / ME</p>
        </div>
      </div>

      <div className="px-4 py-3 border-b border-white/5">
        <div className="flex items-center gap-2 text-xs text-emerald-200/70">
          <Building2 className="w-3.5 h-3.5" />
          <span className="truncate" title={empresa.nome}>{empresa.nome || 'Empresa'}</span>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
        {NAV.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            onClick={onNavigate}
            data-testid={`financas-nav-${to.split('/').pop()}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                isActive
                  ? 'bg-emerald-500/15 text-emerald-200 border border-emerald-500/30'
                  : 'text-slate-400 hover:bg-white/5 hover:text-white'
              }`
            }
          >
            <Icon className="w-4 h-4 shrink-0" />
            <span className="truncate">{label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="px-4 py-3 border-t border-white/5">
        <div className="flex items-center justify-between">
          <div className="text-xs">
            <p className="text-white">{user.nome || 'Usuário'}</p>
            <p className="text-slate-500">@{user.username}</p>
          </div>
          <Button
            data-testid="financas-logout-btn"
            size="sm"
            variant="ghost"
            onClick={onLogout}
            className="text-slate-400 hover:text-red-400 hover:bg-red-500/10"
          >
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </>
  );
}

export default function FinancasLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const empresa = JSON.parse(localStorage.getItem('financas_empresa') || '{}');
  const user = JSON.parse(localStorage.getItem('financas_user') || '{}');

  const logout = () => {
    localStorage.removeItem('financas_token');
    localStorage.removeItem('financas_user');
    localStorage.removeItem('financas_empresa');
    navigate('/financas/login');
  };

  const currentLabel = NAV.find(n => location.pathname.startsWith(n.to))?.label || 'Financeiro';

  return (
    <div className="min-h-screen bg-slate-950 flex">
      {/* Sidebar desktop */}
      <aside className="hidden md:flex w-64 bg-slate-900 border-r border-emerald-500/10 flex-col">
        <NavList empresa={empresa} user={user} onLogout={logout} />
      </aside>

      {/* Topbar mobile */}
      <header className="md:hidden fixed top-0 left-0 right-0 z-30 bg-slate-900/95 backdrop-blur border-b border-white/10 px-4 h-14 flex items-center justify-between">
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button size="sm" variant="ghost" className="text-slate-300 hover:text-white px-2" data-testid="financas-menu-mobile-btn">
              <Menu className="w-5 h-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="bg-slate-900 border-r border-white/10 p-0 w-72 flex flex-col">
            <NavList empresa={empresa} user={user} onLogout={logout} onNavigate={() => setOpen(false)} />
          </SheetContent>
        </Sheet>
        <div className="flex items-center gap-2">
          <Calculator className="w-4 h-4 text-emerald-400" />
          <span className="text-white text-sm font-medium truncate max-w-[180px]">{currentLabel}</span>
        </div>
        <span className="text-emerald-300/60 text-xs truncate max-w-[80px]">{empresa.nome || ''}</span>
      </header>

      {/* Main */}
      <main className="flex-1 overflow-y-auto pt-14 md:pt-0">
        <Outlet />
      </main>
    </div>
  );
}
